import { ReactElement } from 'react'
import { KpiCard, ProgressBar, salesReportIcons } from '../../components/SalesReport'
import { AbandonedCartResponse, ListedStatus } from '../../hooks/abandoned-cart'
import {
  formatCents,
  formatInteger,
  formatMinutes,
  formatPercent,
  METHOD_LABELS,
  REASON_LABELS,
  STATUS_LABELS,
  STEP_LABELS,
} from './labels'
import * as S from './styles'

const STATUS_ORDER: ListedStatus[] = ['never', 'returned', 'refunded', 'refunded_retried']

interface Bar {
  key: string
  label: string
  value: number
}

function BarCard({ title, subtitle, bars, total }: { title: string, subtitle?: string, bars: Bar[], total: number }): ReactElement {
  return (
    <S.Card>
      <S.CardTitle>{title}</S.CardTitle>
      {subtitle && <S.CardSubtitle>{subtitle}</S.CardSubtitle>}
      {bars.length === 0 ? (
        <S.Empty>Nenhum registro no período.</S.Empty>
      ) : (
        <S.BarList>
          {bars.map((bar) => (
            <S.BarRow key={bar.key}>
              <S.BarHead>
                <span>{bar.label}</span>
                <S.BarValue>{formatInteger(bar.value)}</S.BarValue>
              </S.BarHead>
              <ProgressBar
                percentage={total === 0 ? 0 : (bar.value / total) * 100}
                label={`${bar.label}: ${formatInteger(bar.value)}`}
                track="neutral"
                tone="danger"
                height={6}
              />
            </S.BarRow>
          ))}
        </S.BarList>
      )}
    </S.Card>
  )
}

export function ReportContent({ report, mobile }: { report: AbandonedCartResponse, mobile: boolean }): ReactElement {
  const icon = (src: string): string | undefined => (mobile ? undefined : src)
  const { people, recovery, funnel, sessions } = report
  const steps = funnel.steps.filter((step) => funnel.eventPageMeasured || step.step !== 'added_to_cart')
  const started = steps.find((step) => step.step === 'started')?.sessions ?? 0
  const hidden = sessions.anonymous + sessions.unregistered + sessions.ambiguous

  return (
    <S.Content aria-label="Relatório de carrinho abandonado">
      <S.Kpis>
        <KpiCard
          icon={icon(salesReportIcons.ticket)}
          label="Ainda não compraram"
          value={formatInteger(people.listed)}
          caption={`de ${formatInteger(people.started)} pessoas que iniciaram a compra`}
        />
        <KpiCard
          icon={icon(salesReportIcons.check)}
          label="Compraram depois"
          value={formatInteger(people.converted)}
          caption={`${formatPercent(recovery.rate)} voltaram e concluíram sozinhas`}
          tone="positive"
        />
        <KpiCard
          icon={icon(salesReportIcons.calendarDays)}
          label="Tempo até comprar"
          value={formatMinutes(recovery.medianMinutesToPurchase)}
          caption={`mediana · ${formatInteger(recovery.convertedWithinOneHour)} em menos de 1 h`}
        />
        <KpiCard
          icon={icon(salesReportIcons.chartLine)}
          label="Valor em aberto"
          value={formatCents(report.openValueCents)}
          caption="somente ingressos, sem taxas"
        />
      </S.Kpis>

      <S.TwoColumns>
        <BarCard
          title="Situação"
          subtitle="Quem está na lista de recuperação"
          total={people.listed}
          bars={STATUS_ORDER.map((status) => ({ key: status, label: STATUS_LABELS[status], value: people.byStatus[status] })).filter(
            (bar) => bar.value > 0
          )}
        />
        <BarCard
          title="Motivo"
          subtitle="Onde a última tentativa parou"
          total={people.listed}
          bars={report.reasons.map((item) => ({ key: item.reason, label: REASON_LABELS[item.reason], value: item.people }))}
        />
      </S.TwoColumns>

      <S.TwoColumns>
        <BarCard
          title="Etapas da compra"
          subtitle="Sessões no período"
          total={started}
          bars={steps.map((step) => ({ key: step.step, label: STEP_LABELS[step.step], value: step.sessions }))}
        />
        <BarCard
          title="Meio de pagamento"
          subtitle="Último meio escolhido por quem não comprou"
          total={people.listed}
          bars={report.paymentMethods.map((item) => ({ key: item.method, label: METHOD_LABELS[item.method], value: item.people }))}
        />
      </S.TwoColumns>

      {report.utmSources.length > 0 && (
        <BarCard
          title="Origem"
          subtitle="Campanhas de quem não comprou (UTM)"
          total={people.listed}
          bars={report.utmSources.map((item) => ({ key: item.source, label: item.source, value: item.people }))}
        />
      )}

      {!funnel.eventPageMeasured && (
        <S.Footnote>Este evento usa mapa de assentos: a escolha do ingresso ainda não é medida.</S.Footnote>
      )}
      {hidden > 0 && (
        <S.Footnote>
          {`${formatInteger(hidden)} sessões sem login ou sem cadastro identificado entram nas etapas, mas não na lista de contatos.`}
        </S.Footnote>
      )}
      <S.Footnote>
        Quem comprou o ingresso depois, inclusive no PDV, sai da lista automaticamente. Pedidos devolvidos voltam para a lista.
      </S.Footnote>
    </S.Content>
  )
}
